import React, { useEffect, useRef, useState } from 'react';
import { useUserRole } from '../../context/UserRoleContext';
import './AcademyCertificateView.css';
import {
  downloadAcademyCertificatePdf,
  generateAcademyCertificatePdf
} from './academyCertificatePdf';
import { printAcademyCertificate } from './academyCertificatePrint';

function formatCertificateDate(value) {
  if (!value) return '—';

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';

  return new Intl.DateTimeFormat('nb-NO', { dateStyle: 'long' }).format(date);
}

function inspectDownloadFrame() {
  try {
    const isFramed = window.self !== window.top;

    if (!isFramed) {
      return {
        downloadFrame: 'Toppnivåvindu',
        frameSandbox: 'Ikke relevant',
        frameDownloadPermission: 'Ikke relevant'
      };
    }

    const frame = window.frameElement;
    if (!frame) {
      return {
        downloadFrame: 'Iframe; iframe-elementet er ikke tilgjengelig',
        frameSandbox: 'Ukjent',
        frameDownloadPermission: 'Kan ikke undersøkes'
      };
    }

    const sandboxValue = frame.getAttribute('sandbox');
    if (sandboxValue === null) {
      return {
        downloadFrame: 'Iframe',
        frameSandbox: 'Ikke sandbox-begrenset',
        frameDownloadPermission: 'Ingen sandbox-begrensning funnet'
      };
    }

    const sandboxTokens = Array.from(frame.sandbox);
    const allowsDownloads = sandboxTokens.includes('allow-downloads');

    return {
      downloadFrame: 'Iframe',
      frameSandbox: sandboxValue || '(tom sandbox-attributt)',
      frameDownloadPermission: allowsDownloads
        ? 'allow-downloads er til stede'
        : 'allow-downloads mangler; sandbox kan begrense nedlastinger'
    };
  } catch (error) {
    return {
      downloadFrame: 'Kunne ikke undersøkes',
      frameSandbox: 'Ukjent',
      frameDownloadPermission: error?.message || 'Iframe-tilgang ble nektet'
    };
  }
}

function CertificateField({ label, children }) {
  return (
    <div className="academy-certificate-field">
      <dt>{label}</dt>
      <dd>{children || '—'}</dd>
    </div>
  );
}

function ResultValue({ value }) {
  return <span className={value ? 'font-semibold text-emerald-700' : 'font-semibold text-slate-500'}>{value || 'IKKE TESTET'}</span>;
}

const initialDiagnostics = {
  jsPdfCreated: null,
  generation: null,
  blobSize: '—',
  mimeType: '—',
  signature: null,
  ready: false,
  error: '',
  downloadClick: null,
  downloadBlobValid: null,
  downloadBlobSize: '—',
  downloadMimeType: '—',
  downloadUrlCreated: null,
  downloadHref: '—',
  downloadAttribute: '—',
  downloadLinkClick: null,
  downloadClickReturned: null,
  downloadError: '',
  downloadFrame: '—',
  frameSandbox: '—',
  frameDownloadPermission: '—'
};

export default function AcademyCertificateView({
  certificate,
  onClose,
  onPrintFailure
}) {
  const { role } = useUserRole();
  const [printError, setPrintError] = useState('');
  const [pdfError, setPdfError] = useState('');
  const [pdfErrorType, setPdfErrorType] = useState('');
  const [pdfBusy, setPdfBusy] = useState(false);
  const [diagnosticBusy, setDiagnosticBusy] = useState(false);
  const [diagnostics, setDiagnostics] = useState(initialDiagnostics);
  const [testPdfUrl, setTestPdfUrl] = useState('');
  const testPdfUrlRef = useRef('');
  const testPdfBlobRef = useRef(null);
  const testPdfCertificateNumberRef = useRef('');
  const pdfBusyRef = useRef(false);
  const isSuperadmin = role === 'SUPERADMIN';

  useEffect(() => () => {
    if (testPdfUrlRef.current) {
      URL.revokeObjectURL(testPdfUrlRef.current);
    }
  }, []);

  if (!certificate) return null;

  const isRevoked = certificate.status === 'revoked';

  function recordDownloadDiagnostic(update) {
    if (isSuperadmin) {
      setDiagnostics((current) => ({ ...current, ...update }));
    }
  }

  function handlePrint() {
    setPrintError('');
    printAcademyCertificate(certificate, (message) => {
      setPrintError(message);
      onPrintFailure?.(message);
    });
  }

  function handleDownloadPdf() {
    if (isSuperadmin) {
      const frameDetails = inspectDownloadFrame();
      setDiagnostics((current) => ({
        ...current,
        downloadClick: true,
        downloadBlobValid: null,
        downloadBlobSize: '—',
        downloadMimeType: '—',
        downloadUrlCreated: null,
        downloadHref: '—',
        downloadAttribute: '—',
        downloadLinkClick: null,
        downloadClickReturned: null,
        downloadError: '',
        ...frameDetails
      }));
    }

    if (pdfBusyRef.current) return;

    pdfBusyRef.current = true;
    setPdfError('');
    setPdfErrorType('');
    setPdfBusy(true);

    try {
      const validatedAsset = testPdfBlobRef.current
        && testPdfUrlRef.current
        && testPdfCertificateNumberRef.current === certificate.certificate_number
        ? {
            blob: testPdfBlobRef.current,
            objectUrl: testPdfUrlRef.current
          }
        : null;
      downloadAcademyCertificatePdf(
        certificate,
        validatedAsset,
        (event) => {
          if (event.stage === 'blob-ready') {
            recordDownloadDiagnostic({
              downloadBlobValid: event.valid,
              downloadBlobSize: event.size,
              downloadMimeType: event.type
            });
          }

          if (event.stage === 'url-ready') {
            recordDownloadDiagnostic({ downloadUrlCreated: true });
          }

          if (event.stage === 'url-error') {
            recordDownloadDiagnostic({
              downloadUrlCreated: false,
              downloadError: event.message
            });
          }

          if (event.stage === 'link-ready') {
            recordDownloadDiagnostic({
              downloadHref: event.href,
              downloadAttribute: event.download
            });
          }

          if (event.stage === 'link-click-started') {
            recordDownloadDiagnostic({ downloadLinkClick: true });
          }

          if (event.stage === 'link-click-returned') {
            recordDownloadDiagnostic({ downloadClickReturned: true });
          }

          if (event.stage === 'error') {
            recordDownloadDiagnostic({
              downloadError: event.message,
              downloadClickReturned: false
            });
          }
        }
      );
    } catch (error) {
      console.error('Academy certificate PDF download failed:', error);
      setPdfErrorType(error?.stage || 'generation');
      setPdfError(error?.message || 'PDF-en kunne ikke genereres eller lastes ned. Prøv igjen.');
      recordDownloadDiagnostic({
        downloadError: error?.message || 'Ukjent JavaScript-feil.',
        downloadClickReturned: false
      });
    } finally {
      pdfBusyRef.current = false;
      setPdfBusy(false);
    }
  }

  function revokeTestPdfUrl() {
    if (testPdfUrlRef.current) {
      URL.revokeObjectURL(testPdfUrlRef.current);
      testPdfUrlRef.current = '';
    }
    testPdfBlobRef.current = null;
    testPdfCertificateNumberRef.current = '';
    setTestPdfUrl('');
  }

  async function handlePdfDiagnostic() {
    setDiagnosticBusy(true);
    setDiagnostics(initialDiagnostics);
    revokeTestPdfUrl();

    const result = { ...initialDiagnostics };
    let createdPdf = false;

    try {
      const blob = generateAcademyCertificatePdf(certificate, () => {
        createdPdf = true;
      });
      result.jsPdfCreated = createdPdf;
      result.generation = true;
      result.blobSize = blob.size;
      result.mimeType = blob.type;

      const signatureBytes = new Uint8Array(await blob.slice(0, 5).arrayBuffer());
      const signature = String.fromCharCode(...signatureBytes);
      result.signature = signature === '%PDF-';

      if (!result.signature) {
        throw new Error(`PDF-signaturen er ugyldig: ${JSON.stringify(signature)}.`);
      }

      const objectUrl = URL.createObjectURL(blob);
      testPdfUrlRef.current = objectUrl;
      testPdfBlobRef.current = blob;
      testPdfCertificateNumberRef.current = certificate.certificate_number;
      setTestPdfUrl(objectUrl);
      result.ready = true;
    } catch (error) {
      result.jsPdfCreated = createdPdf;
      result.generation = createdPdf ? result.generation : false;
      result.signature = result.signature ?? false;
      result.error = error?.message || 'Ukjent JavaScript-feil under PDF-diagnostikken.';
    } finally {
      setDiagnostics(result);
      setDiagnosticBusy(false);
    }
  }

  function handleOpenTestPdf() {
    if (!testPdfUrl) return;

    const openedWindow = window.open(testPdfUrl, '_blank');
    if (!openedWindow) {
      setDiagnostics((current) => ({
        ...current,
        error: 'Nettleseren blokkerte åpning av test-PDF-en i en ny fane.'
      }));
      return;
    }

    openedWindow.opener = null;
  }

  return (
    <div className="academy-certificate-overlay">
      <header className="academy-certificate-toolbar no-print">
        <div>
          <p className="academy-certificate-toolbar-brand">HMS-innsikt</p>
          <p className="academy-certificate-toolbar-title">Kursbevisvisning</p>
          {printError && (
            <p role="alert" className="mt-2 max-w-xl text-sm text-rose-700">
              {printError}
            </p>
          )}
          {pdfError && (
            <p role="alert" className="mt-2 max-w-xl text-sm text-rose-700">
              {pdfErrorType === 'download'
                ? `Nedlastingsforsøket feilet; nettleseren kan ha blokkert det. ${pdfError}`
                : `PDF-generering feilet. ${pdfError}`}
            </p>
          )}
          {pdfBusy && (
            <p role="status" aria-live="polite" className="mt-2 text-sm text-blue-700">
              Genererer PDF …
            </p>
          )}
        </div>
        <div className="academy-certificate-toolbar-actions">
          <button type="button" onClick={handleDownloadPdf} disabled={pdfBusy}>
            {pdfBusy ? 'Genererer PDF …' : 'Last ned PDF'}
          </button>
          <button type="button" onClick={handlePrint}>
            Skriv ut
          </button>
          <button type="button" className="academy-certificate-close" onClick={onClose}>
            Lukk
          </button>
        </div>
      </header>

      {false && role === 'SUPERADMIN' && (
        <section
          className="no-print mx-auto my-4 max-w-5xl rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-slate-800"
          aria-labelledby="academy-pdf-diagnostics-title"
        >
          <h2 id="academy-pdf-diagnostics-title" className="font-bold">
            PDF-diagnostikk (kun SUPERADMIN)
          </h2>
          <div className="mt-3 flex flex-wrap gap-2">
            <button
              type="button"
              onClick={handlePdfDiagnostic}
              disabled={diagnosticBusy}
              className="rounded-lg border border-amber-700 bg-white px-3 py-2 font-semibold text-amber-900 disabled:opacity-60"
            >
              {diagnosticBusy ? 'Tester PDF …' : 'Test PDF-generering'}
            </button>
            {testPdfUrl && (
              <button
                type="button"
                onClick={handleOpenTestPdf}
                className="rounded-lg border border-slate-300 bg-white px-3 py-2 font-semibold text-slate-700"
              >
                Åpne test-PDF
              </button>
            )}
          </div>
          <dl className="mt-3 grid gap-x-6 gap-y-1 sm:grid-cols-2">
            <div>jsPDF opprettet: <ResultValue value={diagnostics.jsPdfCreated === null ? '' : diagnostics.jsPdfCreated ? 'PASS' : 'FAIL'} /></div>
            <div>PDF-generering: <ResultValue value={diagnostics.generation === null ? '' : diagnostics.generation ? 'PASS' : 'FAIL'} /></div>
            <div>Blob-størrelse: <strong>{diagnostics.blobSize}</strong>{diagnostics.blobSize !== '—' ? ' byte' : ''}</div>
            <div>MIME-type: <strong>{diagnostics.mimeType}</strong></div>
            <div>PDF-signatur: <ResultValue value={diagnostics.signature === null ? '' : diagnostics.signature ? 'PASS' : 'FAIL'} /></div>
            <div>Test-PDF klar for åpning: <strong>{diagnostics.ready ? 'JA' : 'NEI'}</strong></div>
            <div>Klikk på «Last ned PDF»: <ResultValue value={diagnostics.downloadClick === null ? '' : diagnostics.downloadClick ? 'JA' : 'NEI'} /></div>
            <div>Nedlastings-Blob gyldig: <ResultValue value={diagnostics.downloadBlobValid === null ? '' : diagnostics.downloadBlobValid ? 'PASS' : 'FAIL'} /></div>
            <div>Nedlastings-Blob-størrelse: <strong>{diagnostics.downloadBlobSize}</strong>{diagnostics.downloadBlobSize !== '—' ? ' byte' : ''}</div>
            <div>Nedlastings-MIME-type: <strong>{diagnostics.downloadMimeType}</strong></div>
            <div>Blob-URL opprettet: <ResultValue value={diagnostics.downloadUrlCreated === null ? '' : diagnostics.downloadUrlCreated ? 'PASS' : 'FAIL'} /></div>
            <div className="break-all">Nedlastingslenkens href: <strong>{diagnostics.downloadHref}</strong></div>
            <div className="break-all">Nedlastingslenkens download-attributt: <strong>{diagnostics.downloadAttribute}</strong></div>
            <div>link.click() startet: <ResultValue value={diagnostics.downloadLinkClick === null ? '' : diagnostics.downloadLinkClick ? 'JA' : 'NEI'} /></div>
            <div>link.click() returnerte: <ResultValue value={diagnostics.downloadClickReturned === null ? '' : diagnostics.downloadClickReturned ? 'JA' : 'NEI'} /></div>
            <div>Nedlastingskontekst: <strong>{diagnostics.downloadFrame}</strong></div>
            <div>Iframe sandbox: <strong>{diagnostics.frameSandbox}</strong></div>
            <div>Sandbox-nedlastingsrettighet: <strong>{diagnostics.frameDownloadPermission}</strong></div>
          </dl>
          {diagnostics.downloadError && (
            <p role="alert" className="mt-2 break-words text-rose-800">
              JavaScript-feil i nedlastingsflyten: {diagnostics.downloadError}
            </p>
          )}
          {diagnostics.error && (
            <p role="alert" className="mt-2 text-rose-800">
              JavaScript-feil: {diagnostics.error}
            </p>
          )}
        </section>
      )}

      <main className="academy-certificate-stage">
        <article
          className={`academy-certificate-paper${isRevoked ? ' is-revoked' : ''}`}
          aria-label="Kursbevis"
        >
          <div className="academy-certificate-border">
            <div className="academy-certificate-topline">
              <span className="academy-certificate-brand">HMS-innsikt</span>
              <span className={`academy-certificate-status${isRevoked ? ' revoked' : ''}`}>
                {isRevoked ? 'Tilbakekalt' : certificate.status === 'issued' ? 'Utstedt' : certificate.status}
              </span>
            </div>

            <div className="academy-certificate-heading">
              <p className="academy-certificate-eyebrow">Kursbevis</p>
              <h1>{certificate.certificate_display_title || 'Kursbevis'}</h1>
              <p className="academy-certificate-intro">Dette bekrefter at</p>
              <p className="academy-certificate-learner">{certificate.learner_display_name}</p>
              <p className="academy-certificate-completed">
                har fullført kurset
              </p>
              <h2>{certificate.course_title}</h2>
              {certificate.course_version_title && (
                <p className="academy-certificate-version-title">
                  {certificate.course_version_title}
                </p>
              )}
            </div>

            <dl className="academy-certificate-details">
              <CertificateField label="Kursversjon">
                {certificate.course_version_version}
                {certificate.course_version_language_code
                  ? ` · ${certificate.course_version_language_code.toUpperCase()}`
                  : ''}
              </CertificateField>
              <CertificateField label="Fullført">
                {formatCertificateDate(certificate.completion_completed_at)}
              </CertificateField>
              <CertificateField label="Utstedt">
                {formatCertificateDate(certificate.issued_at)}
              </CertificateField>
              <CertificateField label="Gyldig til">
                {certificate.valid_until
                  ? formatCertificateDate(certificate.valid_until)
                  : 'Ingen utløpsdato'}
              </CertificateField>
              <CertificateField label="Sertifikatnummer">
                {certificate.certificate_number}
              </CertificateField>
              <CertificateField label="Status">
                {isRevoked ? 'Tilbakekalt — ikke gyldig' : 'Utstedt'}
              </CertificateField>
            </dl>

            <footer className="academy-certificate-footer">
              <div>
                <span className="academy-certificate-footer-label">Utstedt av</span>
                <strong>{certificate.issuer_display_name || 'HMS-innsikt'}</strong>
              </div>
              {isRevoked && (
                <strong className="academy-certificate-revocation">
                  TILBAKEKALT
                </strong>
              )}
            </footer>
          </div>
        </article>
      </main>
    </div>
  );
}